/**
 * globalErrorTrap — 전역 JS 에러 계측 (uncaught error + unhandled promise rejection).
 *
 * 목적: 실기기/TestFlight처럼 콘솔 로그를 직접 못 보는 환경에서 **JS 레벨** 런타임 에러를 누락 없이
 * 노출한다. `boot()` 최상단에 설치해 부팅 중 에러도 잡는다.
 *  - `onError` 콜백으로 호출자가 log-collector 누적(문의하기 첨부) + 화면 오버레이(흰 화면 대신 원인) 처리.
 *  - 동일 메시지 연속 발생은 1회만 보고(무한 에러 루프가 오버레이/로그를 폭주시키는 것 방지).
 *
 * ⚠ 한계: **네이티브 레벨 크래시(NSException — AdMob/Firebase plist 누락 등)는 JS로 못 잡는다.**
 *   네이티브 크래시는 Xcode Device Logs / Crashlytics로 계측한다(plan Track 1·3 참조).
 */

/** 에러 발생 출처 — onerror(동기 throw) vs unhandledrejection(Promise reject). */
export type GlobalErrorSource = "onerror" | "unhandledrejection";

/** 정규화된 에러 정보 — onError 콜백 페이로드. */
export interface GlobalErrorInfo {
  readonly source: GlobalErrorSource;
  readonly message: string;
}

/**
 * 임의 에러 입력(Error/문자열/ErrorEvent/PromiseRejectionEvent/unknown)을 사람이 읽을 1줄 메시지로
 * 정규화한다 (순수 함수). reason/error 래퍼를 파고들고, 어떤 입력에도 throw하지 않는다.
 */
export function formatGlobalError(source: GlobalErrorSource, error: unknown): string {
  return `[globalError][${source}] ${describe(error)}`;
}

/** 내부 — 에러 객체를 1줄 문자열로. ErrorEvent/RejectionEvent 래퍼를 1단계 언래핑. */
function describe(error: unknown): string {
  if (error == null) return String(error);
  if (error instanceof Error) {
    const stackHead = error.stack?.split("\n")[1]?.trim();
    return stackHead ? `${error.name}: ${error.message} (${stackHead})` : `${error.name}: ${error.message}`;
  }
  if (typeof error === "string") return error;
  if (typeof error === "object") {
    const obj = error as Record<string, unknown>;
    // PromiseRejectionEvent.reason / ErrorEvent.error 래퍼 — 한 단계 파고들어 실에러를 꺼낸다.
    if ("reason" in obj && obj["reason"] != null) return describe(obj["reason"]);
    if ("error" in obj && obj["error"] != null) return describe(obj["error"]);
    if (typeof obj["message"] === "string") return obj["message"];
  }
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

/** installGlobalErrorTrap 옵션 — window/콜백 주입(테스트에서 fake window 가능). */
export interface GlobalErrorTrapConfig {
  /** 대상 window (기본 전역 window). 테스트에서 fake 주입. */
  readonly window: Pick<Window, "addEventListener" | "removeEventListener">;
  /** 정규화된 에러 1건 보고 콜백 (log-collector + 오버레이). 동일 메시지 연속은 억제 후 호출. */
  readonly onError: (info: GlobalErrorInfo) => void;
}

/**
 * 전역 에러 리스너를 등록한다. 반환된 dispose()로 해제(재부팅 시 중복 방지).
 * 동일 메시지 연속 발생은 1회만 onError 호출(무한 루프 폭주 억제) — 다른 메시지가 오면 다시 보고.
 */
export function installGlobalErrorTrap(config: GlobalErrorTrapConfig): () => void {
  let lastKey: string | null = null;
  const report = (source: GlobalErrorSource, raw: unknown): void => {
    const message = formatGlobalError(source, raw);
    if (message === lastKey) return; // 동일 메시지 연속 — 억제
    lastKey = message;
    config.onError({ source, message });
  };
  const onWindowError = (e: Event): void => report("onerror", e);
  const onWindowRejection = (e: Event): void => report("unhandledrejection", e);
  config.window.addEventListener("error", onWindowError);
  config.window.addEventListener("unhandledrejection", onWindowRejection);
  return () => {
    config.window.removeEventListener("error", onWindowError);
    config.window.removeEventListener("unhandledrejection", onWindowRejection);
  };
}

/**
 * 치명적 JS 에러를 화면에 표시한다 — 흰 화면 대신 원인 텍스트를 노출(로그 못 보는 실기기 계측).
 * id로 단일 오버레이를 유지하고 메시지를 누적(여러 에러 확인). DOM 의존이라 단위테스트 제외(§4).
 */
export function showFatalErrorOverlay(message: string, doc: Document = document): void {
  const ID = "global-error-overlay";
  let box = doc.getElementById(ID);
  if (!box) {
    box = doc.createElement("div");
    box.id = ID;
    box.setAttribute(
      "style",
      "position:fixed;left:0;right:0;top:0;z-index:99999;max-height:60vh;overflow:auto;" +
        "background:rgba(120,0,0,0.92);color:#fff;font:12px/1.5 monospace;padding:10px;" +
        "white-space:pre-wrap;word-break:break-word;",
    );
    doc.body.appendChild(box);
  }
  box.textContent = `${box.textContent ?? ""}${message}\n`;
}
